import { acquisitionSurveyPayloadSchema } from '@inkitt/contracts-common';
import { stripeConfig } from '@inkitt/types-common';
import {
  AdProviderTypes,
  DeepLinkOverrideDefinition,
  dynamicPaywallOffer,
  exitIntentPayload,
  galateaNotificationPriorityLiteral,
  galateaNotificationTypeLiteral,
  goldenHourOfferFull,
  harlequinConfigPayload,
  harlequinConfigV2Payload,
  harlequinPricingTestDefinition,
  harlequinUpsellTestDefinition,
  introOfferPayload,
  offerBaselineDefinition,
  offerExperimentDefinition,
  postPurchaseGalateaTvOfferPayload,
  presentationBaselineDefinition,
  presentationExperimentDefinition,
  pricingTestDefinition,
  PushyPaywallDefinition,
  testDynamicPaywallV2Definition,
  UserCallOffer,
  WeekDayHappyHourDefinition,
} from '@inkitt/types-galatea';
import { IToggle, IVariant } from '@unleash/proxy-client-react';
import { z } from 'zod';

/**
 * HOW TO USE THIS FILE
 *
 * 1. Refer to the naming convention here - https://www.notion.so/inkitt/Unleash-Create-a-Feature-Toggle-or-AB-Test-0dea366c955c476eb64432812d52b4b0?pvs=4#1deeda66f0a04756b81a45c889dbb3e0
 * 2. If the flag is for AB Test - add it to - abTestToggleDefinition
 * 3. If the flag is for Feature Toggle - add it to - featureToggleDefinition
 * 4. If the flag is going to have variants - make sure to add the correct zod definition
 * 5. If the flag is NOT going to have variants - make sure to add z.unknown()
 */

export const RESETTING_PROGRESS_TEST_NAME = 'testResettingProgress';

export const galateaABTestToggles = {
  testAuthorSocialProfiles: {
    definition: z.unknown(),
    variants: z.enum(['control', 'social_profiles']),
  },
  testReadingStreaksV2: {
    definition: z.object({
      readingStreaksV2: z.boolean(),
    }),
    variants: z.enum(['default']),
  },
  testAdSkips: {
    definition: z
      .object({
        /** Gates the whole feature. Only the treatment arm sets this, so control-like arms stay off. */
        isEnabled: z.boolean().optional(),
        // NOTE: the client reads skipsPerAd and sends it as `amount` on /earn; the server only clamps
        // to [1, 10] (not tamper-safe — a modified client can farm up to the clamp).
        // TODO(DEV-5196): resolve server-side from this flag + gate /earn behind ad-network SSV.
        /**
         * Number of ad-skips credited per rewarded ad. Optional so a treatment payload of just
         * `{ isEnabled: true }` still validates — the client defaults this to DEFAULT_SKIPS_PER_AD.
         * If it were required, a payload missing/out-of-bounds here would fail zod validation and the
         * safe client would drop the ENTIRE payload (isEnabled included), silently disabling the feature.
         */
        skipsPerAd: z.number().int().min(1).max(10).optional(),
        /**
         * POINTS price of the separate "Go Ad-Free" option shown alongside Ad Skips (0 = free) — same
         * meaning as testPostTimerAd.skipAdCost, read directly in AdWall. NOT the banked-skip unlock
         * cost, which is a fixed client constant (AD_SKIPS_TO_CONTINUE).
         */
        skipAdCost: z.number().int().min(0).max(10).optional(),
        /** Optional cap on skips earnable per day (not yet enforced). */
        dailyEarnCap: z.number().int().min(1).optional(),
      })
      .optional(),
    variants: z.enum(['control', 'ad_skips']),
  },
  testRecommendedPointsPackage: {
    definition: z.object({
      recommendedPackageIndex: z.number().optional(),
    }),
    variants: z.enum(['default']),
  },
  testStripeWebview: {
    definition: z.object({
      enabled: z.boolean().optional(),
      inAppPayment: z.boolean().optional(),
      showIAPLink: z.boolean().optional(),
      isIapDiscountCopyVariant: z
        .object({
          bundle: z
            .object({
              ios: z.string().optional(),
              android: z.string().optional(),
            })
            .optional(),
          title: z.record(z.string(), z.string()).optional(),
        })
        .optional(),
      originalPriceString: z.string().optional(),
      // dual_button_* variants only: the real (non-selected) product the IAP button charges and
      // the strikethrough price anchors to, e.g. com.colt.app.yearly.unlimited.9999. Falls back to
      // selectedPackage when unset, so an unconfigured payload degrades to both dual buttons
      // charging the same product rather than crashing.
      dualButtonAnchorProductId: z
        .object({
          ios: z.string().optional(),
          android: z.string().optional(),
        })
        .optional(),
    }),
    variants: z.enum([
      'control',
      'webview',
      'in_app',
      'web_with_iap',
      'webview_with_iap',
      'web_with_iap_discount_copy',
      'dual_button_a',
      'dual_button_b',
    ]),
  },
  testFakeAndroidAlternativeBillingOnly: {
    definition: z.object({
      isEnabled: z.boolean().optional(),
    }),
    variants: z.enum(['fake_abo_disabled', 'fake_abo_enabled']),
  },
  testRealAndroidAlternativeBillingOnly: {
    definition: z.object({
      isEnabled: z.boolean().optional(),
    }),
    variants: z.enum(['real_abo_disabled', 'real_abo_enabled']),
  },
  testAlternativePaymentMethod: {
    definition: z.unknown(),
    variants: z.enum(['control']),
  },
  testHideImmersiveStoryPopup: {
    definition: z.unknown(),
    variants: z.enum(['control', 'hide']),
  },
  testCountdownButtonShimmer: {
    definition: z.object({
      enableShimmer: z.boolean().optional(),
      shimmerDuration: z.number().optional(),
      shimmerDelay: z.number().optional(),
      shimmerGradientStyle: z.record(z.string(), z.unknown()).optional(),
      shimmerRotation: z.string().optional(),
      shimmerInitialDelay: z.number().optional(),
      shimmerColorsArray: z.array(z.string()).length(5).optional(),
    }),
    variants: z.enum(['default']),
  },
  testPaywallButtonShimmer: {
    definition: z.object({
      enableShimmer: z.boolean().optional(),
      shimmerDuration: z.number().optional(),
      shimmerDelay: z.number().optional(),
      shimmerGradientStyle: z.record(z.string(), z.unknown()).optional(),
      shimmerRotation: z.string().optional(),
      shimmerInitialDelay: z.number().optional(),
      shimmerColorsArray: z.array(z.string()).length(5).optional(),
    }),
    variants: z.enum(['default']),
  },
  testPaywallOnChapterCompletion: {
    definition: z
      .object({
        showAfterChapter: z.number().optional(),
      })
      .optional(),
    variants: z.enum(['default']),
  },
  testPaywallVideoBackground: presentationExperimentDefinition(
    'control_aa',
    'variant',
    'flo',
    'control_flo',
    'aa_flo_variant',
  ),
  testPaywallSocialProof: presentationExperimentDefinition('social_proof_b', 'social_proof_c'),
  testPaywallButtonPulse: presentationExperimentDefinition('aa_variant', 'pulse'),
  [RESETTING_PROGRESS_TEST_NAME]: {
    definition: z
      .object({
        revcatOfferingId: z.string().optional(),
      })
      .optional(),
    variants: z.enum(['control']),
  },
  testPostTimerAd: {
    definition: z.object({
      skipAdCost: z.number().optional(),
      showAdsStartingOnDay: z.number(),
      adProvider: z.enum(AdProviderTypes).optional(),
      waitTimeMinutes: z.number().optional(),
    }),
    variants: z.enum(['control', 'ad_post_timer']),
  },
  testSearchMultiTagFilterRow: {
    definition: z.unknown(),
    variants: z.enum(['control', 'multi_tag_filter_row']),
  },
  testFreeShelfNotificationPrompt: {
    definition: z.object({
      enabled: z.boolean(),
    }),
    variants: z.enum(['control', 'treatment']),
  },
  testAAOne: {
    definition: z.unknown(),
    variants: z.enum(['control', 'variant_a']),
  },
  testAATwo: {
    definition: z.unknown(),
    variants: z.enum(['control', 'variant_a']),
  },
  testAAThree: {
    definition: z.unknown(),
    variants: z.enum(['control', 'variant_a']),
  },
  testWeekdayHappyHours: {
    variants: z.enum(['control', 'weekday_50_percent']),
    definition: WeekDayHappyHourDefinition,
  },
  testPushyPaywall: {
    variants: z.enum(['control', 'with_pushy_paywall', 'with_default_floating_g']),
    definition: PushyPaywallDefinition,
  },
  testDynamicPaywall: {
    definition: z.unknown(),
    variants: z.enum(['control']),
  },
  testDynamicPaywallV2: {
    definition: testDynamicPaywallV2Definition,
    variants: z.enum(['control']),
  },
  testPaywallType: presentationBaselineDefinition,
  // Legacy pricing chain: keeps serving its existing cohorts and stops enrolling at the V5 cutover.
  testPricing: pricingTestDefinition,
  testPricingV2: pricingTestDefinition,
  testPricingV3: pricingTestDefinition,
  testPricingV4: pricingTestDefinition,
  // Offer layer: testPricingV5 is the per-market baseline and each testOffer* flag is one experiment on an
  // `offerBucket` range. Register new ones in `galateaOfferExperimentFlags`.
  testPricingV5: offerBaselineDefinition,
  testOfferGoldenHourV2: offerExperimentDefinition('golden_hour_v2'),
  testOfferPointsLimitedSubscription: offerExperimentDefinition(
    'points_limited_subscription_v2',
    'points_limited_subscription_v3',
  ),
  testOfferSplitTextAndAudiobookV3: offerExperimentDefinition('split_text_and_audiobook__v3'),
  testOfferYearly7499WithFamilyV2: offerExperimentDefinition('yearly_7499_with_family_v2'),
  testOfferPostPurchaseRenewalOffer: offerExperimentDefinition('post_purchase_renewal_offer'),
  testOfferIntlTierPricing: offerExperimentDefinition('intl_tier_0', 'intl_tier_2', 'aa_variant'),
  // Standalone AB test (fully independent of the pricing tests) for the post-purchase CandyJar/GalateaTV
  // cross-sell. In the `weekly_candy_jar_offer` arm the CandyJar offer — configured by this test's own
  // payload — is shown right AFTER the audiobook upsell or the renewal offer whenever one of those runs,
  // and standalone otherwise. It carries its own offer payload (instead of reusing the pricing test's
  // `postPurchaseGalateaTvOffer`) precisely because a pricing variant that sets that field is mutually
  // exclusive with the audio/renewal offers this test chains after.
  testCandyJarUpsell: {
    definition: postPurchaseGalateaTvOfferPayload.optional(),
    variants: z.enum(['control', 'aa_weekly_candy_jar_offer', 'weekly_candy_jar_offer']),
  },
  testPointsUpsell: {
    definition: z
      .object({
        daysSinceInstallThreshold: z.number(),
        showAfterAlreadyPurchased: z.boolean(),
      })
      .optional(),
    variants: z.string(),
  },
  testHarlequinPricing: harlequinPricingTestDefinition,
  /**
   * whether the post-purchase Harlequin upsell runs, and the offer it sells. Harlequin's own
   * availability and pricing come from the always-on `featureHarlequinPricing` flag, so only the upsell
   * is an experiment. Eligibility is read from payload presence rather than the arm name, matching
   * `testCandyJarUpsell` — arm names are not a reliable treatment signal.
   */
  testHarlequinUpsell: harlequinUpsellTestDefinition,
  test3MonthsIntroMonthly: {
    definition: z.object({
      introOfferConfig: introOfferPayload.optional(),
      exitIntentConfig: exitIntentPayload.optional(),
    }),
    variants: z.enum(['control']),
  },
  testPricingIOS: {
    definition: z.object({
      revcatOfferingId: z.string(),
    }),
    variants: z.enum(['control', 'aa_variant_1_ios']),
  },
  testPricingAndroid: {
    definition: z.object({
      revcatOfferingId: z.string(),
    }),
    variants: z.enum(['control', 'aa_variant_1_android']),
  },
  testCustomOneshots: {
    definition: z.object({
      shelfItems: z.array(
        z.object({
          coverUrl: z.string(),
          themeSlug: z.string(),
        }),
      ),
      pollingIntervalMs: z.number(),
      pollingDurationMs: z.number(),
    }),
    variants: z.enum(['control', 'customOneshots', 'waitlist']),
  },
  testGuestPass: {
    definition: z.unknown(),
    variants: z.enum(['control', 'free_trial', 'free_book']),
  },
  testApplovinAdjustCallback: {
    definition: z.unknown(),
    variants: z.enum(['control', 'allowCallback']),
  },
  testGTVPromotion: {
    definition: z.object({ titleKey: z.string().optional() }),
    variants: z.enum(['default', 'control', 'with_gtv_shelf', 'control_DE', 'with_gtv_shelf_DE']),
  },
  testLimitedScreenCapture: {
    definition: z.object({
      screenshots: z
        .object({
          type: z.enum(['limited', 'unlimited', 'disabled']),
          allowedScreenshotsPerDay: z.number().default(10),
          showProgressMessage: z.boolean().default(false),
          showDisabledMessage: z.boolean().default(false),
        })
        .optional(),
      video: z
        .object({
          type: z.enum(['limited', 'unlimited', 'disabled']),
          allowedVideoCaptureMsPerDay: z.number().default(6_000),
          showProgressMessage: z.boolean().default(false),
          showDisabledMessage: z.boolean().default(false),
        })
        .optional(),
    }),
    variants: z.enum(['control', 'limited_screenshots_10']),
  },
  testOverrideStoryData: {
    definition: DeepLinkOverrideDefinition,
    variants: z.enum(['control', 'override_story_data']),
  },
  testPinkRebrand: {
    definition: z.unknown(),
    variants: z.enum(['control', 'rebrand']),
  },
  testOnboardingFlow: {
    definition: z.object({
      shouldUseClipboardDeepLinkSolution: z.boolean().optional(),
      discoveryEndpointVersion: z.enum(['v3', 'v4']).default('v3'),
      tags: z
        .array(
          z.object({
            tag_slug: z.string(),
            tag_name: z.string().optional(),
            display_name: z.string().optional(),
          }),
        )
        .optional(),
      hideSignupForDeepLinkUsers: z.boolean().optional(),
      hideSignupForNonDeepLinkUsers: z.boolean().optional(),
      showSignupForNonDeepLinkUsers: z.boolean().optional(),
      isHoldoutGroup: z.boolean().optional(),
      showBookSelectorScreen: z.boolean().optional(),
      isRebranding: z.boolean().optional(),
    }),
    variants: z.enum([
      'control',
      'organic_cltv',
      'organic_cltv_additional_metrics',
      'organic_cltv_more_shelves',
      'book_selector',
      'rebranding',
      'genreshelves_treatment',
      'ch10Completion',
      'social_proof',
      'banner_treatment',
    ]),
  },
  testPrePermissionPNPrompt: {
    definition: z
      .object({
        isEnabled: z.boolean().optional(),
        promptLocation: z.enum(['pre_genre', 'post_genre']).optional(),
      })
      .optional(),
    variants: z.enum(['control', 'pre_genre_selector', 'post_genre_selector']),
  },
  testRenewalBannerType: {
    definition: z.object({
      bannerType: z.optional(z.enum(['v2'])),
    }),
    variants: z.enum(['control']),
  },
  testSignUpDuringDeepLink: {
    definition: z.object({
      hideSignupForDeepLinkUsers: z.boolean().optional(),
      hideSignupForNonDeepLinkUsers: z.boolean().optional(),
      isEligibleForAndroidDiscount: z.boolean().optional(),
      onboardingFlowConfig: z
        .object({
          tags: z
            .array(
              z.object({
                tag_slug: z.string(),
                tag_name: z.string().optional(),
                display_name: z.string().optional(),
              }),
            )
            .optional(),
          showOnlyIntroScreen: z.boolean().optional(),
          showBookSelectorScreen: z.boolean().optional(),
        })
        .optional(),
    }),
    variants: z.enum(['control', 'with_signup', 'intro_screen', 'book_selector']),
  },
  testFloatingGForCancelledSubscribers: {
    definition: z.unknown(),
    variants: z.enum(['control', 'floating_g']),
  },
  testEndOfBookRecommendation: {
    definition: z.unknown(),
    variants: z.enum(['control', 'endOfBookRecommendation']),
  },
  testUserAcquisitionSurvey: {
    definition: acquisitionSurveyPayloadSchema,
    variants: z.enum(['control', 'survey']),
  },
  testCandyjarSocials: {
    definition: z.object({
      instagramUrl: z.string(),
    }),
    variants: z.enum(['candyjar', 'galateatv']),
  },
  testWeb2AppInstallFlow: {
    definition: z.unknown(),
    variants: z.enum(['control', 'continue_app', 'continue_reading']),
  },
  testDelayedStripeSubscription: {
    definition: z
      .object({
        shouldDelaySubscriptions: z.boolean().optional(),
      })
      .optional(),
    variants: z.enum(['default']),
  },
  testAppToStripeHostedCheckout: {
    definition: z.unknown(),
    variants: z.enum(['control', 'hosted']),
  },
  testHostedCheckout: {
    definition: z.unknown(),
    variants: z.enum(['control', 'hosted']),
  },
  testCommentsInReaderV3: {
    definition: z.object({ isEnabled: z.boolean() }),
    variants: z.enum(['control', 'comments_in_reader', 'aa_variant']),
  },
  testPointsForEmailVerification: {
    definition: z.object({
      pointsAmount: z.number(),
    }),
    variants: z.enum(['with_coins']),
  },
  testTranslatePostAndComments: {
    definition: z.unknown(),
    variants: z.enum(['control', 'translatePostAndComments']),
  },
  testBannerAdInReader: {
    definition: z.object({
      shouldShowHideButton: z.boolean().optional(),
      shouldHideLiquidGlass: z.boolean().optional(),
      numberOfAds: z.number().optional(),
      adShowDurationMs: z.number().optional(),
      adHiddenDurationMs: z.number().optional(),
      showAdsStartingOnDay: z.number().optional(),
      showAdAfterCountdownStartingOnDay: z.number().optional(),
      adProvider: z.enum(AdProviderTypes).optional(),
    }),
    variants: z.enum(['control']),
  },
  testSupportAuthor: {
    definition: z.unknown(),
    variants: z.enum(['control']),
  },
  testListenNowForTextBook: {
    definition: z.object({
      isListenNowForTextBookActive: z.boolean().optional(),
      requiresActiveSubscription: z.boolean().optional(),
    }),
    variants: z.enum(['control', 'listen_now_for_textbook', 'listen_now_after_subscription']),
  },
  testMandatoryAccountAfterStripeSubscription: {
    definition: z.unknown(),
    variants: z.enum(['control', 'mandatory_sign_in']),
  },
  testDiscoveryCommunityTab: {
    definition: z.object({
      enabled: z.boolean(),
      authorId: z.string(),
    }),
    variants: z.enum(['control', 'withCommunityTab']),
  },
  testPinkRebrandPhase2: {
    definition: z.object({
      isEnabled: z.boolean(),
    }),
    variants: z.enum(['control', 'rebrand']),
  },
  testCandyJarPromotion: {
    definition: z.object({
      isEnabled: z.boolean(),
      /**
       * Coins shown in the post-subscription coins toast (first app open after a subscription
       * purchase). Display value only — the granted amount is authoritative on the backend
       * (`galatea-subscriber-coins` reward). Defaults to 500 in `useTestCandyJarPromotion`.
       */
      subscriptionCoins: z.number().optional(),
      /**
       * Coins shown in the in-reader adapted-book toasts (chapter 15 / book finished). Display value
       * only — the backend grant is authoritative. Defaults to 50 in `useTestCandyJarPromotion`.
       */
      readerCoins: z.number().optional(),
    }),
    variants: z.enum(['control', 'with_candyjar_promotion']),
  },
  testAccountCreationOnCountdown: {
    definition: z.object({
      showStartingOnDay: z.number().optional(),
    }),
    variants: z.enum(['control', 'with_account_creation']),
  },
  testSubscriptionPlaceholderBanner: {
    definition: z
      .object({
        // Banner is rendered after the discovery shelf at this section index (0-based).
        afterShelfIndex: z.number().int().nonnegative(),
      })
      .optional(),
    variants: z.enum(['control', 'with_banner']),
  },
  testCountdownBottomSheet: {
    definition: z.object({
      isEnabled: z.boolean().optional(),
    }),
    variants: z.enum(['control', 'aa_bottom_sheet', 'bottom_sheet']),
  },
  // sign-in sheet at the first spicy scene. Enrolment and treatment branch on
  // the exact arm name, never on isControl/isVariant: those are substring heuristics that
  // misread aa_ arms.
  testSpicyChapterSignIn: {
    // Optional so control and aa_sign_in_sheet stay valid with no payload configured.
    // Without it their empty payload fails the parse and useSafeVariant reports them as
    // 'disabled' — indistinguishable from the 85% outside the test.
    definition: z
      .object({
        isEnabled: z.boolean().optional(),
      })
      .optional(),
    variants: z.enum(['control', 'aa_sign_in_sheet', 'sign_in_sheet']),
  },
  /**
   * DEV-6978: countdown and paywall merged into one sheet. The treatment arm replaces the
   * countdown sheet's CTA stack with the unified subscription + points paywall, and the separate
   * `openBuyModal` trip to the Shop screen goes away. Independent of `testCountdownBottomSheet`:
   * this arm brings the sheet with it, so a user can be in it without being in that test.
   */
  testCountdownMergedPaywall: {
    definition: z.object({
      isEnabled: z.boolean().optional(),
    }),
    variants: z.enum(['control', 'aa_merged_paywall', 'merged_paywall']),
  },
  /**
   * Audiobooks Plus trial pack: 340 points for $0.99, offered once to users who have never
   * purchased. Lives in the brand's OWN Unleash instance (`unleash-audiobooks.inkitt.com`, see
   * `cashcow.config.ts`), so it simply never exists for Galatea and stays on the disabled bootstrap
   * there.
   *
   * No payload on purpose. The credited amount belongs to the SKU (client prints it, Jupiter grants
   * it), so putting it in a payload would let Unleash and the wallet drift apart; and the audience
   * rule ("no purchases yet") is not something we want retunable without a release.
   */
  testAudiobooksTrialPack: {
    definition: z.unknown(),
    variants: z.enum(['control', 'trial_pack']),
  },
  /**
   * iOS-only disclosure sheet shown right before the app hands a purchase over to the web
   * (Stripe) checkout. Mirrors the Android Alternative-Billing dialog gate: the treatment arm
   * has to acknowledge the sheet before the browser/webview opens, the control arm redirects
   * straight away. `isEnabled` gates the sheet so a treatment-shaped arm can still ship as a
   * no-op.
   */
  testExternalPurchaseDisclosure: {
    definition: z.object({
      isEnabled: z.boolean().optional(),
    }),
    variants: z.enum(['control', 'with_disclosure_sheet']),
  },
};
export type GalateaABTestToggles = typeof galateaABTestToggles;

export const OFFER_BASELINE_FLAG = 'testPricingV5' satisfies keyof GalateaABTestToggles;

export const galateaOfferExperimentFlags = [
  'testOfferGoldenHourV2',
  'testOfferPointsLimitedSubscription',
  'testOfferSplitTextAndAudiobookV3',
  'testOfferYearly7499WithFamilyV2',
  'testOfferPostPurchaseRenewalOffer',
  'testOfferIntlTierPricing',
] as const satisfies readonly (keyof GalateaABTestToggles)[];

export type GalateaOfferExperimentFlag = (typeof galateaOfferExperimentFlags)[number];

export const PRESENTATION_BASELINE_FLAG = 'testPaywallType' satisfies keyof GalateaABTestToggles;

export const galateaPresentationExperimentFlags = [
  'testPaywallVideoBackground',
  'testPaywallSocialProof',
  'testPaywallButtonPulse',
] as const satisfies readonly (keyof GalateaABTestToggles)[];

export type GalateaPresentationExperimentFlag = (typeof galateaPresentationExperimentFlags)[number];

export const galateaFeatureFlagDefinition = {
  featureAdVotingForPoints: {
    definition: z.object({
      url: z.string(),
      targetPositionIndex: z.number().default(5),
      pointsAmount: z.number().default(250),
      isNew: z.boolean().optional(),
    }),
    variants: z.enum(['default']),
  },
  featureGalateaSocialLinks: {
    definition: z.object({
      facebookPageUrl: z.string().min(1).optional(),
      instagramUrl: z.string().min(1).optional(),
    }),
    variants: z.enum(['default']),
  },
  featureInAppAppleWinbackPopups: {
    definition: z.object({
      showAppleWinbackOffers: z.boolean().optional(),
    }),
    variants: z.enum(['default']),
  },
  /**
   * Carries the golden-hour offer configuration. Saturn reads this feature toggle when the
   * client signals `isGoldenHourAvailable: true` (set via the `golden_hour` variant of
   * `testPricingV4`). The payload is a full golden-hour offer — see `goldenHourOfferFull`.
   */
  featureGoldenHour: {
    definition: goldenHourOfferFull,
    variants: z.enum(['default']),
  },
  featureWebPayment: {
    definition: stripeConfig,
    variants: z.enum(['default']),
  },
  /**
   * Always-on Harlequin subscription config (offering / SKU / optional Stripe + IAP-link settings).
   * Present for every reader regardless of `testHarlequinPricing` bucketing, so anyone who reaches a
   * Harlequin book can resolve a purchasable offering instead of an endless paywall spinner. The
   * experiment's payload still overrides this default for enrolled treatment users.
   */
  featureHarlequinPricing: {
    definition: harlequinConfigPayload,
    variants: z.enum(['default']),
  },
  /**
   * the Galatea × Harlequin bundle. Supersedes `featureHarlequinPricing` for readers it is on
   * for — same config plus the bundle products — and is the single switch for the feature: resolving its
   * payload is what puts the bundle on the paywall and opens Harlequin to readers with no Galatea
   * subscription. A second flag rather than new fields on the old one because shipped payloads are
   * immutable; the old flag stays as the fallback until this one is fully rolled out.
   */
  featureHarlequinPricingV2: {
    definition: harlequinConfigV2Payload,
    variants: z.enum(['default']),
  },
  featureGalateaWebSearch: {
    definition: z.unknown(),
    variants: z.enum(['default']),
  },
  /** Gates the DEV-6371 redeem app-switch prompt. Off hides it for redeemed buyers. */
  featureRedeemAppSwitchPrompt: {
    definition: z.unknown(),
    variants: z.enum(['default']),
  },
  featureAdvancedDisplaySettings: {
    definition: z.unknown(),
    variants: z.enum(['default']),
  },
  /** DEV-7382: adds the OpenDyslexic option to the reader Display pane font row. Off falls back to classic. */
  featureReaderDyslexicFont: {
    definition: z.unknown(),
    variants: z.enum(['default']),
  },
  featureUserTransfer: {
    definition: z.unknown(),
    variants: z.enum(['default']),
  },
  featureGalateaWrapUpNotification: {
    definition: z.object({
      notificationKey: z.string(),
      titleString: z.string(),
      bodyString: z.string(),
    }),
    variants: z.enum(['default']),
  },
  featureGalateaWrapUp: {
    definition: z.object({
      webviewBaseUrl: z.string(),
      defaultBackgroundColor: z.string().optional(),
      newTagStatus: z.enum(['default', 'off']).optional(),
      currentFlashbackDate: z.string().optional(),
      isSummaryShareEnabled: z.boolean().optional(),
    }),
    variants: z.enum(['default']),
  },
  // DEV-6496 — one trigger paragraph per series, addressed by stable ids rather than an
  // ordinal: after a re-edit an ordinal still resolves, to the WRONG paragraph, while an id
  // pair simply stops matching. That is what req 2.5's "no longer resolves" asks for.
  // A feature toggle, not the AB payload, so all three arms read the same placements and
  // emit the same exposure.
  featureSpicyChapterPlacements: {
    definition: z
      .object({
        placements: z.record(
          // Series id. Localized editions are distinct series, so language falls out of the key.
          z.string(),
          z.object({
            // Episode NUMBER, matching the reader's episodeId and the sheet's "chapter" column.
            episode: z.number().int().positive(),
            // Section id; contentDataId is only unique within a section, so both are required.
            // Positive because the reader treats only ids > 0 as real rows: a 0 or negative
            // id would parse, never match, and surface only as an unresolved-placement event.
            contentId: z.number().int().positive(),
            contentDataId: z.number().int().positive(),
          }),
        ),
      })
      .optional(),
    variants: z.enum(['default']),
  },
  featureFreeBookShelf: {
    definition: z
      .object({
        freeBookShelf: z.record(
          z.string(),
          z.array(
            z.object({
              id: z.string(),
              start: z.number(),
              end: z.number(),
            }),
          ),
        ),
      })
      .optional(),
    variants: z.enum(['default']),
  },
  featureFloatingG: {
    definition: z
      .object({
        showInReader: z.boolean().optional(),
      })
      .optional(),
    variants: z.enum(['default']),
  },
  featureTrackingServiceConfig: {
    definition: z.object({
      batchSize: z.number(),
      pollingRate: z.number(),
      trackingLevel: z.number(),
    }),
    variants: z.enum(['default']),
  },
  featureReaderFeedback: {
    definition: z.boolean(),
    variants: z.enum(['default']),
  },
  featureAuthorPageWallEnableWallPostLinks: {
    definition: z.boolean(),
    variants: z.enum(['default']),
  },
  featureAuthorCommunitiesList: {
    definition: z.boolean(),
    variants: z.enum(['default']),
  },
  featureAuthorFollowAutomaticallyAfterChapters: {
    definition: z.object({
      followAtEpisode: z.number(),
    }),
    variants: z.enum(['default']),
  },
  featureAuthorWallActivityBadges: {
    definition: z.boolean(),
    variants: z.enum(['default']),
  },
  featurePromoCodes: {
    definition: z.unknown(),
    variants: z.enum(['control', 'disabled']),
  },
  author_page: {
    definition: z.unknown(),
    variants: z.enum(['default']),
  },
  author_page_wall: {
    definition: z.unknown(),
    variants: z.enum(['default']),
  },
  locally_scheduled_notifications: {
    definition: z.object({
      titleKey: z.string(),
      bodyKey: z.string(),
      urlKey: z.string(),
      nonSubscribedUsersOnly: z.boolean(),
      subscribersOnly: z.boolean(),
      cadenceInMinutes: z.number().array(),
    }),
    variants: z.enum(['default']),
  },
  feature_create_account_web: {
    definition: z.object({
      coupon: z.string(),
    }),
    variants: z.enum(['default']),
  },
  author_page_audiobooks: {
    definition: z.unknown(),
    variants: z.enum(['default']),
  },
  author_page_web_link: {
    definition: z.unknown(),
    variants: z.enum(['default']),
  },
  display_trigger_warnings_from_studio: {
    definition: z.unknown(),
    variants: z.enum(['default']),
  },
  audibookTabHidden: {
    definition: z.boolean(),
    variants: z.enum(['default']),
  },
  featureUserCallOffers: {
    definition: UserCallOffer.array(),
    variants: z.enum(['default']),
  },
  featureEnableDefaultForOldPricingTest: {
    definition: z.any(),
    variants: z.enum(['default']),
  },
  autoFollowToggle: {
    definition: z.array(z.string()),
    variants: z.enum(['default']),
  },
  featureNotificationCenter: {
    definition: z.any(),
    variants: z.enum(['default']),
  },
  featureCommentsPoint: {
    definition: z.any(),
    variants: z.enum(['default']),
  },
  featureCreateAccountV2: {
    definition: z.any(),
    variants: z.enum(['default']),
  },
  featureProseWebview: {
    definition: z.any(),
    variants: z.enum(['default']),
  },
  featureReferralProgramSettings: {
    definition: z.any(),
    variants: z.enum(['default']),
  },
  featureReferFriendProgram: {
    definition: z.any(),
    variants: z.enum(['default']),
  },
  featureStreakPrize: {
    definition: z.any(),
    variants: z.enum(['default']),
  },
  featureOnboardingTags: {
    definition: z.any(),
    variants: z.enum(['default']),
  },
  featureComingSoonAvailableLanguages: {
    definition: z.any(),
    variants: z.enum(['default']),
  },
  featureManageSubscriptionPromotionalOffer: {
    definition: z.any(),
    variants: z.enum(['default']),
  },
  featureUpdatePopup: {
    definition: z.any(),
    variants: z.enum(['default']),
  },
  featureNewTabShelvesIdsByLocalMap: {
    definition: z.any(),
    variants: z.enum(['default']),
  },
  featureUnsubscribeUserCallModal: {
    definition: z.object({
      minimumDaysBetweenShowing: z.number(),
    }),
    variants: z.enum(['default']),
  },
  featureCancelationSurveyV2: {
    definition: z.object({
      surveyId: z.string(),
      surveyIdForMonthlySubscribers: z.string().optional(),
      surveyDisplayLimitDays: z.number(),
    }),
    variants: z.enum(['default']),
  },
  featureSaleBooks: {
    definition: z.any(),
    variants: z.enum(['default']),
  },
  featureWebTest: {
    definition: z.any(),
    variants: z.enum(['default']),
  },
  featureDynamicStoryPreferences: {
    definition: z.object({
      dimensions: z.array(
        z.object({
          slug: z.string(),
          label: z.string(),
          values: z.array(
            z.object({
              id: z.number(),
              label: z.string(),
              icon: z.string(),
            }),
          ),
          default: z.number(),
        }),
      ),
    }),
    variants: z.enum(['default']),
  },
  dynamicPaywallOffers: {
    definition: z.array(dynamicPaywallOffer),
    variants: z.enum(['default']),
  },
  featurePrintBookNotification: {
    definition: z.object({
      config: z.record(
        z.string(),
        z.object({
          minChapterRead: z.number(),
          language: z.array(z.string()).optional(),
          country: z.array(z.string()).optional(),
          notificationConfig: z.object({
            title: z.string(),
            body: z.string(),
            type: z.enum(galateaNotificationTypeLiteral),
            key: z.string(),
            priority: z.enum(galateaNotificationPriorityLiteral),
            metadata: z.record(z.string(), z.unknown()),
          }),
        }),
      ),
    }),
    variants: z.enum(['default']),
  },
  additionalOnboardingSteps: {
    definition: z.object({
      isSpecialOfferEnabled: z.boolean(),
      isPointsReviewEnabled: z.boolean(),
      areUserCallsEnabled: z.boolean(),
    }),
    variants: z.enum(['default']),
  },
  featureCommentReplies: {
    definition: z.any(),
    variants: z.enum(['default']),
  },
  featureBlockedUsers: {
    definition: z.unknown(),
    variants: z.enum(['default']),
  },
  featureUserProfile: {
    definition: z.any(),
    variants: z.enum(['default']),
  },
  featureOldVersionsBlocking: {
    definition: z.any(),
    variants: z.enum(['default']),
  },
  featureAndroidAlternativeBillingOnly: {
    definition: z.unknown(),
    variants: z.enum(['default']),
  },
  featurePublicReadingLists: {
    definition: z.unknown(),
    variants: z.enum(['default']),
  },
  featureUniversalControl: {
    definition: z.unknown(),
    variants: z.enum(['default']),
  },
  featureRenewalsHoldout: {
    definition: z.array(z.string()),
    variants: z.enum(['default']),
  },
  featureAIConcierge: {
    definition: z.object({
      ai_concierge_url: z.string(),
    }),
    variants: z.enum(['default']),
  },
  featureSaturnUnlockSystem: {
    definition: z.any(),
    variants: z.enum(['default']),
  },
  featureSaturnPromotionalOffer: {
    definition: z.unknown(),
    variants: z.enum(['default']),
  },
  featureSaturnReferralCode: {
    definition: z.unknown(),
    variants: z.enum(['default']),
  },
  featureExploreMore: {
    definition: z.unknown(),
    variants: z.enum(['default']),
  },
  featureStripeSubscriptionCancelCallPopup: {
    definition: z.unknown(),
    variants: z.enum(['default']),
  },
  featureFunnelFoxStoryMap: {
    definition: z.record(z.string(), z.string()),
    variants: z.enum(['default']),
  },
  featureAudiobookVideoCovers: {
    definition: z.record(z.string(), z.string()),
    variants: z.enum(['default']),
  },
  featureForce1xOnVideoCovers: {
    definition: z.object({ maxTotalMemoryGB: z.number().optional() }).optional(),
    variants: z.enum(['default']),
  },
  safeAdjectives: {
    definition: z.object({
      safeAdjectives: z.string().array(),
    }),
    variants: z.enum(['default']),
  },
  featurePinListToSearch: {
    definition: z.object({
      lists: z.array(z.number()),
    }),
    variants: z.enum(['default']),
  },
  featureCommunitySearchCategory: {
    definition: z.object({
      authorIds: z.array(z.string()),
    }),
    variants: z.enum(['default']),
  },
  featureCommunityStoriesCount: {
    definition: z.object({
      overrides: z.array(
        z.object({
          authorId: z.string(),
          count: z.number(),
        }),
      ),
    }),
    variants: z.enum(['default']),
  },
  featureHealth: {
    definition: z.any(),
    variants: z.enum(['default']),
  },
  featureIterableCommunityWorkflow: {
    definition: z.object({
      config: z.record(
        z.string(),
        z.object({
          iterableWorkflowId: z.number(),
          communityAuthorIds: z.array(z.string()).optional(),
        }),
      ),
    }),
    variants: z.enum(['default']),
  },
  featureSummaryPageEnlargeImage: {
    definition: z.boolean(),
    variants: z.enum(['default']),
  },
  featureDontWaitInitialRemoteProgress: {
    definition: z.object({
      howMinutesUserToBeOld: z.number(),
    }),
    variants: z.enum(['default']),
  },
  featureStripeAccountCreationRedirect: {
    definition: z.object({
      active: z.number(),
    }),
    variants: z.enum(['default']),
  },
  featureHideAuthorTipWidget: {
    definition: z.object({
      authorIds: z.array(z.string()),
    }),
    variants: z.enum(['default']),
  },
  featureCommunityWallBadges: {
    definition: z.object({
      authorIds: z.array(z.string()),
    }),
    variants: z.enum(['default']),
  },
  featureHarlequinWeb: {
    definition: z.unknown(),
    variants: z.enum(['default']),
  },
  /**
   * Gates the redesigned Harlequin checkout LP. Separate from `featureHarlequinWeb`, which
   * decides whether the Harlequin LP exists at all: the redesign has to be switchable on its own so
   * the shipped $7 standalone flow can stay up while it rolls out.
   */
  featureHarlequinLPRedesign: {
    definition: z.unknown(),
    variants: z.enum(['default']),
  },
  featureAdultContentPolicy: {
    definition: z.object({
      isAdult: z.boolean(),
    }),
    variants: z.enum(['default']),
  },
  /**
   * Turns auto unlock ON by default for every series, and drops its Points+ subscription
   * requirement (DEV-6362). Meant for Audiobooks Plus, which sells no subscription at all — points
   * are its only currency, so the countdown is pure friction for a user who can already pay.
   *
   * Each brand polls its own Unleash instance, so enabling this for the audio brand cannot reach
   * Galatea. A user's explicit per-series choice still wins; see `autoUnlockService`.
   */
  featureAutoUnlockByDefault: {
    definition: z.unknown(),
    variants: z.enum(['default']),
  },
  featureSEOCategoryEnrichment: {
    definition: z.object({
      treatmentSlugs: z.array(z.string()),
    }),
    variants: z.enum(['default']),
  },
  featureRevenueCatProxy: {
    definition: z.object({
      revenueCatProxy: z.union([z.boolean(), z.enum(['true', 'false'])]),
    }),
    variants: z.enum(['default']),
  },
};

export const galateaFlags: typeof galateaFeatureFlagDefinition & typeof galateaABTestToggles = {
  ...galateaFeatureFlagDefinition,
  ...galateaABTestToggles,
};

export type GalateaAllFlags = typeof galateaFlags;

export type GalateaFeatureKey = keyof typeof galateaFeatureFlagDefinition;
export type GalateaFlagKey = keyof typeof galateaFlags;
export type GalateaABTestKey = keyof typeof galateaABTestToggles;

export type GalateaVariantPayloadType<K extends keyof typeof galateaFlags> = z.infer<
  (typeof galateaFlags)[K]['definition']
>;

const buildBootstrapValue = (
  flagName: keyof typeof galateaFlags,
  isEnabled: boolean,
  defaultVariantPayload?: IVariant['payload'],
): IToggle => {
  return {
    enabled: isEnabled,
    name: flagName,
    impressionData: false,
    variant: {
      enabled: !!defaultVariantPayload,
      name: 'disabled',
      payload: defaultVariantPayload,
    },
  };
};

/**
 * Bootstrap values are used for flags when for some reason the client is not able to fetch the flags from Unleash Server.
 *
 * If you feature flag has a important configuration data, make sure to set the proper default value below. Example -
 *
 * flagName: buildBootstrapValue('flagName', true, {
 *    type: 'json',
 *    value: JSON.stringify({
 *      configProperty1: true,
 *      configProperty2: false,
 *   })
 * })
 */
const unleashFeatureToggleBootStrapMap: Record<keyof typeof galateaFeatureFlagDefinition, IToggle> = {
  featureAdVotingForPoints: buildBootstrapValue('featureAdVotingForPoints', false),
  featureGalateaSocialLinks: buildBootstrapValue('featureGalateaSocialLinks', false),
  // Off when Unleash is unreachable: a wrong `true` here would spend a user's points without them
  // ever having asked for it.
  featureAutoUnlockByDefault: buildBootstrapValue('featureAutoUnlockByDefault', false),
  featureInAppAppleWinbackPopups: buildBootstrapValue('featureInAppAppleWinbackPopups', false),
  featureGoldenHour: buildBootstrapValue('featureGoldenHour', false),
  featureUserTransfer: buildBootstrapValue('featureUserTransfer', false),
  featureAdvancedDisplaySettings: buildBootstrapValue('featureAdvancedDisplaySettings', false),
  featureReaderDyslexicFont: buildBootstrapValue('featureReaderDyslexicFont', false),
  featureGalateaWrapUpNotification: buildBootstrapValue('featureGalateaWrapUpNotification', false),
  featureGalateaWrapUp: buildBootstrapValue('featureGalateaWrapUp', false),
  featureCommunitySearchCategory: buildBootstrapValue('featureCommunitySearchCategory', false),
  featureHealth: buildBootstrapValue('featureHealth', false),
  featureWebTest: buildBootstrapValue('featureWebTest', false),
  featureAuthorPageWallEnableWallPostLinks: buildBootstrapValue('featureAuthorPageWallEnableWallPostLinks', false),
  featureAuthorCommunitiesList: buildBootstrapValue('featureAuthorCommunitiesList', false),
  featureAuthorFollowAutomaticallyAfterChapters: buildBootstrapValue(
    'featureAuthorFollowAutomaticallyAfterChapters',
    false,
  ),
  featureAuthorWallActivityBadges: buildBootstrapValue('featureAuthorWallActivityBadges', false),
  author_page: buildBootstrapValue('author_page', false),
  author_page_wall: buildBootstrapValue('author_page_wall', false),
  locally_scheduled_notifications: buildBootstrapValue('locally_scheduled_notifications', false),
  feature_create_account_web: buildBootstrapValue('feature_create_account_web', false),
  author_page_audiobooks: buildBootstrapValue('author_page_audiobooks', false),
  author_page_web_link: buildBootstrapValue('author_page_web_link', false),
  display_trigger_warnings_from_studio: buildBootstrapValue('display_trigger_warnings_from_studio', false),
  audibookTabHidden: buildBootstrapValue('audibookTabHidden', false),
  featureUserCallOffers: buildBootstrapValue('featureUserCallOffers', false),
  featureEnableDefaultForOldPricingTest: buildBootstrapValue('featureEnableDefaultForOldPricingTest', false),
  featureReaderFeedback: buildBootstrapValue('featureReaderFeedback', false),
  featureTrackingServiceConfig: buildBootstrapValue('featureTrackingServiceConfig', false),
  autoFollowToggle: buildBootstrapValue('autoFollowToggle', false),
  featureNotificationCenter: buildBootstrapValue('featureNotificationCenter', false),
  featureCommentsPoint: buildBootstrapValue('featureCommentsPoint', false),
  featureCreateAccountV2: buildBootstrapValue('featureCreateAccountV2', false),
  featureFreeBookShelf: buildBootstrapValue('featureFreeBookShelf', false),
  // Off with no payload: if Unleash is unreachable the resolver sees no placements and no
  // sheet fires, which is req 2.5's fail-silent posture.
  featureSpicyChapterPlacements: buildBootstrapValue('featureSpicyChapterPlacements', false),
  featureProseWebview: buildBootstrapValue('featureProseWebview', false),
  featureFloatingG: buildBootstrapValue('featureFloatingG', true, {
    type: 'json',
    value: JSON.stringify({
      showInReader: true,
    }),
  }),
  featureReferralProgramSettings: buildBootstrapValue('featureReferralProgramSettings', true, {
    type: 'json',
    value: JSON.stringify({ senderCoinsBonus: 500, receiverCoinsBonus: 0 }),
  }),
  featureReferFriendProgram: buildBootstrapValue('featureReferFriendProgram', false),
  featureStreakPrize: buildBootstrapValue('featureStreakPrize', true, {
    type: 'json',
    value: JSON.stringify({ week: [1, 1, 1, 1, 1, 1, 5], special_days: { '20': 50 } }),
  }),
  featureOnboardingTags: buildBootstrapValue('featureOnboardingTags', true, {
    type: 'json',
    value: JSON.stringify([]),
  }),
  featureComingSoonAvailableLanguages: buildBootstrapValue('featureComingSoonAvailableLanguages', false),
  featureManageSubscriptionPromotionalOffer: buildBootstrapValue('featureManageSubscriptionPromotionalOffer', false),
  featureUpdatePopup: buildBootstrapValue('featureUpdatePopup', false),
  featureNewTabShelvesIdsByLocalMap: buildBootstrapValue('featureNewTabShelvesIdsByLocalMap', false),
  featureUnsubscribeUserCallModal: buildBootstrapValue('featureUnsubscribeUserCallModal', false),
  featureCancelationSurveyV2: buildBootstrapValue('featureCancelationSurveyV2', false),
  featureSaleBooks: buildBootstrapValue('featureSaleBooks', false),
  featureDynamicStoryPreferences: buildBootstrapValue('featureDynamicStoryPreferences', false),
  dynamicPaywallOffers: buildBootstrapValue('dynamicPaywallOffers', false),
  featurePrintBookNotification: buildBootstrapValue('featurePrintBookNotification', false),
  featureCommentReplies: buildBootstrapValue('featureCommentReplies', false),
  featureBlockedUsers: buildBootstrapValue('featureBlockedUsers', false),
  featureOldVersionsBlocking: buildBootstrapValue('featureOldVersionsBlocking', false),
  featureUserProfile: buildBootstrapValue('featureUserProfile', false),
  featureAndroidAlternativeBillingOnly: buildBootstrapValue('featureAndroidAlternativeBillingOnly', false),
  featurePublicReadingLists: buildBootstrapValue('featurePublicReadingLists', false),
  featureUniversalControl: buildBootstrapValue('featureUniversalControl', false),
  featureRenewalsHoldout: buildBootstrapValue('featureRenewalsHoldout', false),
  featureAIConcierge: buildBootstrapValue('featureAIConcierge', false),
  featureSaturnUnlockSystem: buildBootstrapValue('featureSaturnUnlockSystem', false),
  featureSaturnPromotionalOffer: buildBootstrapValue('featureSaturnPromotionalOffer', false),
  featureSaturnReferralCode: buildBootstrapValue('featureSaturnReferralCode', false),
  featureExploreMore: buildBootstrapValue('featureExploreMore', false),
  featureStripeSubscriptionCancelCallPopup: buildBootstrapValue('featureStripeSubscriptionCancelCallPopup', false),
  featureFunnelFoxStoryMap: buildBootstrapValue('featureFunnelFoxStoryMap', false),
  featureAudiobookVideoCovers: buildBootstrapValue('featureAudiobookVideoCovers', false),
  // Off when Unleash is unreachable: playback then behaves exactly as today (user-chosen rate, button visible).
  featureForce1xOnVideoCovers: buildBootstrapValue('featureForce1xOnVideoCovers', false),
  safeAdjectives: buildBootstrapValue('safeAdjectives', false),
  featurePinListToSearch: buildBootstrapValue('featurePinListToSearch', false),
  featureCommunityStoriesCount: buildBootstrapValue('featureCommunityStoriesCount', false),
  featurePromoCodes: buildBootstrapValue('featurePromoCodes', false),
  featureIterableCommunityWorkflow: buildBootstrapValue('featureIterableCommunityWorkflow', false),
  featureSummaryPageEnlargeImage: buildBootstrapValue('featureSummaryPageEnlargeImage', false),
  additionalOnboardingSteps: buildBootstrapValue('additionalOnboardingSteps', false),
  featureWebPayment: buildBootstrapValue('featureWebPayment', false),
  featureHarlequinPricingV2: buildBootstrapValue('featureHarlequinPricingV2', false),
  featureHarlequinPricing: buildBootstrapValue('featureHarlequinPricing', true, {
    type: 'json',
    value: JSON.stringify({
      revcatOfferingId: 'monthly_harlequin_textonly_799',
      sku: 'hlqn-1m-1',
      showIAPLink: true,
    }),
  }),
  featureGalateaWebSearch: buildBootstrapValue('featureGalateaWebSearch', false),
  featureRedeemAppSwitchPrompt: buildBootstrapValue('featureRedeemAppSwitchPrompt', false),
  featureDontWaitInitialRemoteProgress: buildBootstrapValue('featureDontWaitInitialRemoteProgress', false, {
    type: 'json',
    value: JSON.stringify({ howMinutesUserToBeOld: 60 }),
  }),
  featureStripeAccountCreationRedirect: buildBootstrapValue('featureStripeAccountCreationRedirect', false, {
    type: 'json',
    value: JSON.stringify({ active: true }),
  }),
  featureHideAuthorTipWidget: buildBootstrapValue('featureHideAuthorTipWidget', false),
  featureCommunityWallBadges: buildBootstrapValue('featureCommunityWallBadges', false),
  featureHarlequinWeb: buildBootstrapValue('featureHarlequinWeb', false),
  featureHarlequinLPRedesign: buildBootstrapValue('featureHarlequinLPRedesign', false),
  featureAdultContentPolicy: buildBootstrapValue('featureAdultContentPolicy', true, {
    type: 'json',
    value: JSON.stringify({ isAdult: true }),
  }),
  featureSEOCategoryEnrichment: buildBootstrapValue('featureSEOCategoryEnrichment', false),
  featureRevenueCatProxy: buildBootstrapValue('featureRevenueCatProxy', false),
};

/**
 * For all AB Test flags, we bootstrap the value as though they are turned off
 */
const unleashABTestBootstrap: IToggle[] = Object.keys(galateaABTestToggles).map((abTestKey) => ({
  enabled: false,
  name: abTestKey,
  impressionData: false,
  variant: {
    enabled: false,
    name: 'disabled',
    payload: undefined,
  },
}));

export const galateaBootstrapData: IToggle[] = [
  ...unleashABTestBootstrap,
  ...Object.values(unleashFeatureToggleBootStrapMap),
];

export type GalateaFlagVariants<K extends GalateaFlagKey> =
  | z.infer<(typeof galateaFlags)[K]['variants']>
  | 'control'
  | 'disabled';
